import { useEffect, useState } from "react";
import { X } from "@phosphor-icons/react";

const MATERIAL_FIELDS = new Set([
  "title", "slug", "excerpt", "article_markdown", "meta_title",
  "meta_description", "cta_text", "cta_url", "author_name",
  "original_insight_source",
]);

export default function ContentEngineDraftEditor({ item, onClose, onSave, saving }) {
  const [form, setForm] = useState({});

  useEffect(() => {
    if (!item) return;
    setForm({
      title: item.title || "",
      slug: item.slug || "",
      excerpt: item.excerpt || "",
      article_markdown: item.article_markdown || "",
      meta_title: item.meta_title || "",
      meta_description: item.meta_description || "",
      cta_text: item.cta_text || "Run Your Business Growth Assessment",
      cta_url: item.cta_url || "/check",
      author_name: item.author_name || "RankTrust Editorial",
      editorial_approved: item.editorial_approval_revision === item.revision_digest,
      expected_revision_digest: item.revision_digest,
      original_insight_source: item.original_insight_source || "",
    });
  }, [item]);

  if (!item) return null;

  const set = (key, value) => setForm((current) => ({
    ...current,
    [key]: value,
    ...(MATERIAL_FIELDS.has(key) ? { editorial_approved: false } : {}),
  }));
  const submit = (e) => { e.preventDefault(); onSave(form); };
  const failures = item.quality_gate?.failures || [];

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <form onSubmit={submit} className="bg-white w-full max-w-5xl max-h-[92vh] overflow-y-auto rounded-lg shadow-xl p-6 space-y-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="label-tiny">Content Engine Draft</div>
            <h2 className="font-display text-2xl font-bold">Review & Edit</h2>
          </div>
          <button type="button" aria-label="Close editor" onClick={onClose}><X size={20} /></button>
        </div>

        {failures.length > 0 && (
          <div className="border border-amber-200 bg-amber-50 p-4 rounded-md">
            <div className="font-bold text-sm">Quality gate needs attention</div>
            <div className="text-xs text-amber-800 mt-1">{failures.join(" • ")}</div>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Title"><input className="input-field w-full" value={form.title || ""} onChange={(e) => set("title", e.target.value)} /></Field>
          <Field label="Slug"><input className="input-field w-full font-mono" value={form.slug || ""} onChange={(e) => set("slug", e.target.value)} /></Field>
        </div>

        <Field label="Excerpt"><textarea className="input-field w-full min-h-20" value={form.excerpt || ""} onChange={(e) => set("excerpt", e.target.value)} /></Field>
        <Field label="Article"><textarea className="input-field w-full min-h-[420px] font-mono text-sm" value={form.article_markdown || ""} onChange={(e) => set("article_markdown", e.target.value)} /></Field>

        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Meta title"><input className="input-field w-full" value={form.meta_title || ""} onChange={(e) => set("meta_title", e.target.value)} /></Field>
          <Field label="CTA text"><input className="input-field w-full" value={form.cta_text || ""} onChange={(e) => set("cta_text", e.target.value)} /></Field>
        </div>
        <Field label="Meta description"><textarea className="input-field w-full min-h-20" value={form.meta_description || ""} onChange={(e) => set("meta_description", e.target.value)} /></Field>
        <Field label="CTA URL"><input className="input-field w-full font-mono" value={form.cta_url || ""} onChange={(e) => set("cta_url", e.target.value)} /></Field>
        <div className="border border-zinc-200 rounded-lg p-4 space-y-4">
          <h3 className="font-bold">SEO, authority & editorial review</h3>
          <p className="text-sm text-zinc-600">Before approval, include two contextual links to real RankTrust pages and a link to an authoritative source relevant to your factual claims. Open each external source and verify the claim; an AI-generated link is not evidence that its claim is correct.</p>
          <Field label="Visible byline"><input className="input-field w-full" value={form.author_name || ""} onChange={(e) => set("author_name", e.target.value)} /></Field>
          <Field label="Original RankTrust insight source (optional; only use verified, non-identifying assessment findings)"><input className="input-field w-full" placeholder="Internal report/source ID or verifiable provenance" value={form.original_insight_source || ""} onChange={(e) => set("original_insight_source", e.target.value)} /></Field>
          <fieldset className="border-t border-zinc-200 pt-4 space-y-3">
            <legend className="font-semibold text-sm pt-3">Human editorial approval — one confirmation</legend>
            <p className="text-xs text-zinc-600">Review this current article for originality, factual accuracy and verified sources, truthful authorship, reader usefulness, and unnecessary duplication. Technical Health Check is scored separately. This approval applies to legacy drafts too and is cleared after material edits.</p>
            <label className="flex gap-3 items-start text-sm">
              <input type="checkbox" className="mt-1"
                checked={!!form.editorial_approved}
                onChange={(e) => set("editorial_approved", e.target.checked)}
              />
              <span>I personally completed all editorial checks for this exact article revision, including verifying each external citation against its nearby claim.</span>
            </label>
          </fieldset>
          {item.seo_review_required && <p className="text-xs text-amber-800">SEO review is mandatory for newly generated articles. Save, recheck quality and approve only after the editorial checks pass.</p>}
        </div>

        <div className="flex justify-end gap-2">
          <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-primary" type="submit" disabled={saving}>{saving ? "Saving…" : "Save Draft"}</button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return <div><label className="label-tiny block mb-1">{label}</label>{children}</div>;
}
